import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Typography,
} from "../../../../components";
import { useCacheContext } from "../../../../contexts/cache.context";
import { filtrosUrl } from "../../../../helpers";
import { DreService, SessionService } from "../../../../services";
import { cacheApi, formatPrice, responseErros } from "../../../../utils";
import { filtrosIniciais } from "./dashboard-dre";
import { DreEvolucaoChart, DreFiltros, DreSummaryCards } from "./components";

const sessionService = new SessionService();

const DashboardDre = () => {
  const { cacheDto, setCacheDto } = useCacheContext();
  const dreService = new DreService();
  const usuario = sessionService.getUsuario();
  const empresa = sessionService.getEmpresa();
  const acessosEmpresas = usuario?.acessosEmpresas || [];
  const filtrosPadrao = {
    ...filtrosIniciais,
    empresas: empresa ? [empresa] : [],
  };

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filtrosPadrao);
  const [filtros, setFiltros] = useState(filtrosPadrao);
  const empresasIds = (filtros.empresas ?? []).map((item) => item.id);
  const empresasIdsKey = empresasIds.join(",");
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openRows, setOpenRows] = useState({});

  const activeCount =
    (acessosEmpresas.length > 0 ? empresasIds.length : 0) +
    (filtros.periodo !== filtrosIniciais.periodo ? 1 : 0) +
    (filtros.ano !== filtrosIniciais.ano ? 1 : 0);

  const abrirFiltros = () => {
    setDraft(filtros);
    setOpen(true);
  };

  const colunasMap = {
    MENSAL: [
      "Janeiro",
      "Fevereiro",
      "Março",
      "Abril",
      "Maio",
      "Junho",
      "Julho",
      "Agosto",
      "Setembro",
      "Outubro",
      "Novembro",
      "Dezembro",
    ],
    BIMESTRAL: ["1º Bim", "2º Bim", "3º Bim", "4º Bim", "5º Bim", "6º Bim"],
    TRIMESTRAL: ["1º Tri", "2º Tri", "3º Tri", "4º Tri"],
    SEMESTRAL: ["1º Sem", "2º Sem"],
    ANUAL: ["Anual"],
  };

  const gruposMap = {
    MENSAL: [[0], [1], [2], [3], [4], [5], [6], [7], [8], [9], [10], [11]],
    BIMESTRAL: [
      [0, 1],
      [2, 3],
      [4, 5],
      [6, 7],
      [8, 9],
      [10, 11],
    ],
    TRIMESTRAL: [
      [0, 1, 2],
      [3, 4, 5],
      [6, 7, 8],
      [9, 10, 11],
    ],
    SEMESTRAL: [
      [0, 1, 2, 3, 4, 5],
      [6, 7, 8, 9, 10, 11],
    ],
    ANUAL: [[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]],
  };

  const colunas = colunasMap[filtros.periodo] || colunasMap.MENSAL;
  const grupos = gruposMap[filtros.periodo] || gruposMap.MENSAL;

  const agregarValores = (valores) =>
    grupos.map((grupo) => grupo.reduce((s, i) => s + (valores[i] ?? 0), 0));

  const mapApiParaRows = (data) => {
    const por = (tipo) => data.filter((item) => item.tipo === tipo);

    const buildFixedSubgroup = (items, id, label) => {
      const valores = Array(12).fill(0);
      items.forEach((item) =>
        (item.valores || []).forEach((v, i) => {
          valores[i] = Math.floor((valores[i] + v) * 100) / 100;
        }),
      );
      return {
        id,
        conta: label,
        type: "subgroup",
        valores,
        valoresAgregados: agregarValores(valores),
        total: valores.reduce((s, v) => s + v, 0),
        children: [],
      };
    };

    const somarValores = (items) => {
      const valores = Array(12).fill(0);
      items.forEach((item) =>
        (item.valores || []).forEach((v, i) => {
          valores[i] += v;
        }),
      );
      return valores;
    };

    const buildLinhaPlano = (item) => {
      const vals = item.valores || Array(12).fill(0);
      return {
        id: `plano-${item.planoFinanceiroId}`,
        conta: item.planoFinanceiro,
        type: "line",
        valores: vals,
        valoresAgregados: agregarValores(vals),
        total: vals.reduce((s, v) => s + v, 0),
      };
    };

    const agruparPorContaSuperior = (items) => {
      const linhas = [];
      items.forEach((item) => {
        const contaSuperiorId = `plano-superior-${item.planoSuperiorId}`;
        if (!item.planoSuperiorId) {
          linhas.push(buildLinhaPlano(item));
        } else if (!linhas.some((linha) => linha.id === contaSuperiorId)) {
          const subordinadas = items.filter(
            (outro) => outro.planoSuperiorId === item.planoSuperiorId,
          );
          const valores = somarValores(subordinadas);
          linhas.push({
            id: contaSuperiorId,
            conta: item.planoSuperior,
            type: "superior",
            valores,
            valoresAgregados: agregarValores(valores),
            total: valores.reduce((s, v) => s + v, 0),
          });
          subordinadas.forEach((subordinada) =>
            linhas.push({ ...buildLinhaPlano(subordinada), subordinada: true }),
          );
        }
      });
      return linhas;
    };

    const buildExpandableGroup = (items, id, label) => {
      const valores = somarValores(items);
      return {
        id,
        conta: label,
        type: "group",
        valores,
        valoresAgregados: agregarValores(valores),
        total: valores.reduce((s, v) => s + v, 0),
        children: agruparPorContaSuperior(items),
      };
    };

    const buildCalculated = (id, conta, valores) => ({
      id,
      conta,
      type: "calculated",
      valores,
      valoresAgregados: agregarValores(valores),
      total: valores.reduce((s, v) => s + v, 0),
      children: [],
    });

    const buildSingleLine = (item, id, label) => {
      const vals = item?.valores || Array(12).fill(0);
      return {
        id,
        conta: label,
        type: "line",
        valores: vals,
        valoresAgregados: agregarValores(vals),
        total: vals.reduce((s, v) => s + v, 0),
      };
    };

    const vendaRows = por("VENDA_PRODUTO").map((item) =>
      buildFixedSubgroup(
        [item],
        `subgroup-venda-produtos-${item.planoFinanceiroId}`,
        item.planoFinanceiro,
      ),
    );
    const servicoRow = buildFixedSubgroup(
      por("PRESTACAO_SERVICO"),
      "subgroup-prestacao-servicos",
      "Prestação de Serviços",
    );

    const receitaBrutaChildren = [...vendaRows, servicoRow];
    const receitaBrutaValores = receitaBrutaChildren.reduce((acc, curr) => {
      return curr.valores.map((valor, index) => valor + acc[index]);
    }, Array(12).fill(0));
    const receitaBrutaRow = {
      id: "receita-bruta-vendas",
      conta: "Receita Bruta de Vendas",
      type: "group",
      valores: receitaBrutaValores,
      valoresAgregados: agregarValores(receitaBrutaValores),
      total: receitaBrutaValores.reduce((s, v) => s + v, 0),
      children: receitaBrutaChildren,
    };

    const descontoProdutoLine = buildSingleLine(
      por("DESCONTO_PRODUTO")[0],
      "desconto-produto",
      "Descontos de Venda de Produtos",
    );
    const descontoServicoLine = buildSingleLine(
      por("DESCONTO_SERVICO")[0],
      "desconto-servico",
      "Descontos de Prestação de Serviços",
    );
    const descontoProdutoServicoLine = buildSingleLine(
      por("DESCONTO_PRODUTO_SERVICO")[0],
      "desconto-produto-servico",
      "Descontos de Produtos na O.S",
    );
    const devolucaoLine = buildSingleLine(
      por("DEVOLUCAO")[0],
      "devolucao",
      "Devoluções",
    );
    const deducoesValores = descontoProdutoLine.valores.map(
      (v, i) =>
        v +
        descontoServicoLine.valores[i] +
        descontoProdutoServicoLine.valores[i] +
        devolucaoLine.valores[i],
    );
    const deducoesRow = {
      id: "deducoes-receita-bruta",
      conta: "(-) Deduções da Receita Bruta",
      type: "group",
      valores: deducoesValores,
      valoresAgregados: agregarValores(deducoesValores),
      total: deducoesValores.reduce((s, v) => s + v, 0),
      children: [
        descontoProdutoLine,
        descontoServicoLine,
        descontoProdutoServicoLine,
        devolucaoLine,
      ],
    };

    const faturamentoLiquidoValores = receitaBrutaValores.map(
      (v, i) => v - deducoesValores[i],
    );
    const faturamentoLiquidoRow = buildCalculated(
      "faturamento-liquido",
      "= Faturamento Líquido",
      faturamentoLiquidoValores,
    );

    const cmvLine = buildSingleLine(
      por("CMV")[0],
      "cmv-mercadorias",
      "CMV - Custo das Mercadorias Vendidas",
    );

    const cmvOrdemServicoLine = buildSingleLine(
      por("CMV_ORDEM_SERVICO")[0],
      "cmv-ordem-servico",
      "CMV - Produtos de Ordem de Serviço",
    );

    const custosVariaveisExpandivel = buildExpandableGroup(
      por("CUSTO_VARIAVEL"),
      "custos-variaveis-planos",
      "(-) Custos / Despesas Variáveis",
    );

    const custosVariaveisValores = cmvLine.valores.map(
      (v, i) =>
        v +
        cmvOrdemServicoLine.valores[i] +
        custosVariaveisExpandivel.valores[i],
    );
    const custosVariaveisRow = {
      id: "custos-variaveis",
      conta: "(-) Custos / Despesas Variáveis",
      type: "group",
      valores: custosVariaveisValores,
      valoresAgregados: agregarValores(custosVariaveisValores),
      total: custosVariaveisValores.reduce((s, v) => s + v, 0),
      children: [
        cmvLine,
        cmvOrdemServicoLine,
        ...custosVariaveisExpandivel.children,
      ],
    };

    const margemValores = faturamentoLiquidoValores.map(
      (v, i) => v - custosVariaveisRow.valores[i],
    );
    const margemRow = buildCalculated(
      "margem-contribuicao",
      "= Margem de Contribuição / Lucro Bruto",
      margemValores,
    );

    const custosFixosRow = buildExpandableGroup(
      por("CUSTO_FIXO"),
      "custos-fixos",
      "(-) Despesas Fixas / Operacionais",
    );

    const lucroValores = margemValores.map(
      (v, i) => v - custosFixosRow.valores[i],
    );
    const lucroRow = buildCalculated(
      "lucro-liquido",
      "= Lucro Líquido Operacional",
      lucroValores,
    );

    return [
      receitaBrutaRow,
      deducoesRow,
      faturamentoLiquidoRow,
      custosVariaveisRow,
      margemRow,
      custosFixosRow,
      lucroRow,
    ];
  };

  const rows = useMemo(
    () => (rawData.length ? mapApiParaRows(rawData) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawData, filtros.periodo],
  );

  useEffect(() => {
    buscarDashboardDre();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.ano, empresasIdsKey]);

  async function buscarDashboardDre() {
    setLoading(true);
    const label = filtrosUrl.getUrl();
    const cacheBody = {
      dto: { cacheDto, setCacheDto },
      timeMinutes: 15,
      apiFunction: dreService.getReceitaMensal({
        ano: filtros.ano,
        empresasIds: empresasIds.length ? empresasIds : undefined,
      }),
    };
    const result = await cacheApi(
      `${label}-${filtros.ano}-${empresasIdsKey}`,
      cacheBody,
    );
    if (!result.isAxiosError) {
      setRawData(result.data);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const getIndicatorColor = (id, type) => {
    if (type === "calculated") return "#2563eb";
    const greenIds = ["receita-bruta-vendas", "subgroup-prestacao-servicos"];
    const redIds = [
      "deducoes-receita-bruta",
      "desconto-produto",
      "desconto-servico",
      "devolucao",
      "custos-variaveis",
      "cmv-mercadorias",
      "cmv-ordem-servico",
      "custos-fixos",
      "custos-variaveis-planos",
    ];
    if (greenIds.includes(id) || id.includes("subgroup-venda-produtos"))
      return "#16a34a";
    if (redIds.includes(id) || id.startsWith("plano-")) return "#dc2626";
    return "#94a3b8";
  };

  const toggleRow = (id) => {
    setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <Card className="minimal-layout-card">
      <CardContent className="minimal-layout-card-content rolagem">
        <Box className="w-100 ms-auto" sx={{ pb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
            gap={2}
          >
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                DRE — Demonstrativo de Resultados
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Visão consolidada de receitas, margens e resultado do exercício.
              </Typography>
            </Box>

            <Badge
              badgeContent={activeCount}
              color="primary"
              overlap="rectangular"
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={abrirFiltros}
                startIcon={<i className="ph-fill ph-sliders-horizontal" />}
                sx={{ whiteSpace: "nowrap" }}
              >
                Filtros
              </Button>
            </Badge>
          </Box>

          <DreFiltros
            filtros={filtros}
            filtrosPadrao={filtrosPadrao}
            onAplicar={setFiltros}
            acessosEmpresas={acessosEmpresas}
            draft={draft}
            setDraft={setDraft}
            open={open}
            setOpen={setOpen}
          />
        </Box>
        <DreSummaryCards rows={rows} loading={loading} />
        {rows.length > 0 && (
          <>
            <DreEvolucaoChart rows={rows} colunas={colunas} loading={loading} />
          </>
        )}
        <Card
          sx={{
            borderRadius: 3,
            border: "1px solid #f0f0f0",
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              Demonstrativo de Resultados
            </Typography>
            {loading ? (
              <Box>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={300}
                  sx={{ borderRadius: 2 }}
                />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          backgroundColor: "#f8fafc",
                          minWidth: "20rem",
                        }}
                      >
                        CATEGORIA
                      </TableCell>
                      {colunas.map((m) => (
                        <TableCell
                          key={m}
                          className="text-uppercase"
                          align="right"
                          sx={{ fontWeight: 700, minWidth: "8rem" }}
                        >
                          {m}
                        </TableCell>
                      ))}
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, minWidth: "8rem" }}
                      >
                        TOTAL
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rawData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={colunas.length + 2}
                          align="center"
                          sx={{ py: 4, color: "text.secondary" }}
                        >
                          Nenhum dado encontrado para o período selecionado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => {
                        const isCalculated = row.type === "calculated";
                        const isLucro = row.id === "lucro-liquido";
                        const lucroColor = isLucro
                          ? row.total >= 0
                            ? "#16a34a"
                            : "#dc2626"
                          : "inherit";
                        return (
                          <Fragment key={row.id}>
                            <TableRow
                              sx={{
                                backgroundColor: isCalculated
                                  ? "#f1f5f9"
                                  : "inherit",
                                borderTop: isCalculated
                                  ? "2px solid #e2e8f0"
                                  : "none",
                                "&:hover td": {
                                  backgroundColor: isCalculated
                                    ? "#e8edf3"
                                    : "#f0f4f8",
                                },
                              }}
                            >
                              <TableCell
                                onClick={() => toggleRow(row.id)}
                                sx={{
                                  cursor:
                                    row.children?.length > 0
                                      ? "pointer"
                                      : "default",
                                  minWidth: "20rem",
                                  fontWeight: 700,
                                  color: lucroColor,
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 1,
                                  backgroundColor: isCalculated
                                    ? "#f1f5f9"
                                    : "#fff",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 4,
                                      minWidth: 4,
                                      height: 18,
                                      borderRadius: 4,
                                      backgroundColor: getIndicatorColor(
                                        row.id,
                                        row.type,
                                      ),
                                      display: "inline-block",
                                      flexShrink: 0,
                                    }}
                                  />
                                  {!isCalculated && row.children?.length > 0 ? (
                                    <IconButton size="small">
                                      <i
                                        className={`ph ph-caret-${
                                          openRows[row.id] ? "down" : "right"
                                        }`}
                                      />
                                    </IconButton>
                                  ) : (
                                    <span style={{ width: 28 }} />
                                  )}
                                  <span>{row.conta}</span>
                                </div>
                              </TableCell>
                              {row.valoresAgregados.map((v, i) => (
                                <TableCell
                                  key={i}
                                  align="right"
                                  sx={{
                                    fontWeight: isCalculated ? 700 : 500,
                                    color: isLucro
                                      ? v >= 0
                                        ? "#16a34a"
                                        : "#dc2626"
                                      : "inherit",
                                  }}
                                >
                                  {formatPrice(v, false)}
                                </TableCell>
                              ))}
                              <TableCell
                                align="right"
                                sx={{ fontWeight: 700, color: lucroColor }}
                              >
                                {formatPrice(row.total)}
                              </TableCell>
                            </TableRow>

                            {!isCalculated &&
                              row.children?.length > 0 &&
                              openRows[row.id] &&
                              row.children.map((child) => (
                                <TableRow
                                  key={child.id}
                                  sx={{
                                    "&:hover td": {
                                      backgroundColor: "#f0f4f8",
                                    },
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      color: "#374151",
                                      fontWeight:
                                        child.type === "superior" ? 600 : 400,
                                      position: "sticky",
                                      left: 0,
                                      zIndex: 1,
                                      backgroundColor: "#fff",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        paddingLeft: child.subordinada
                                          ? 32
                                          : 16,
                                      }}
                                    >
                                      <span
                                        style={{
                                          width: 4,
                                          minWidth: 4,
                                          height: 14,
                                          borderRadius: 4,
                                          backgroundColor: getIndicatorColor(
                                            child.id,
                                            child.type,
                                          ),
                                          opacity: 0.45,
                                          display: "inline-block",
                                          flexShrink: 0,
                                        }}
                                      />
                                      {child.conta}
                                    </div>
                                  </TableCell>
                                  {child.valoresAgregados.map((v, i) => (
                                    <TableCell
                                      key={i}
                                      align="right"
                                      sx={{
                                        fontWeight:
                                          child.type === "superior" ? 600 : 400,
                                      }}
                                    >
                                      {formatPrice(v, false)}
                                    </TableCell>
                                  ))}
                                  <TableCell
                                    align="right"
                                    sx={{
                                      fontWeight:
                                        child.type === "superior" ? 600 : 500,
                                    }}
                                  >
                                    {formatPrice(child.total)}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
};

export default DashboardDre;
